import React, { useState, useEffect } from "react";
import { fetchImages, getImageUrl } from "../lib/api";
import type { Photo } from "../lib/api";
import { ImageModal } from "./ImageModal";
import { appConfig } from "../config/app.config";

export function Gallery() {
  const [images, setImages] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  
  const limit = appConfig.gallery.itemsPerPage;

  const loadImages = async (pageNum: number) => {
    try {
      console.log(`🔄 Loading images for page ${pageNum}...`);
      setLoading(true);
      setError(null);
      
      const data = await fetchImages(limit, pageNum);
      console.log(`📊 Received ${data.length} images for page ${pageNum}`);
      
      // Handle empty response gracefully
      if (data.length === 0 && pageNum === 1) {
        console.log('📭 No images available');
        setError('Brak dostępnych zdjęć. Sprawdź połączenie z serwerem lub spróbuj ponownie później.');
        setHasMore(false);
      } else {
        if (pageNum === 1) {
          setImages(data);
          console.log('🔄 Set initial images');
        } else {
          setImages(prev => {
            const newImages = [...prev, ...data];
            console.log(`📊 Total images after page ${pageNum}: ${newImages.length}`);
            return newImages;
          });
        }
        setHasMore(data.length === limit);
        console.log(`📊 Has more pages: ${data.length === limit}`);
      }
    } catch (err) {
      console.error('❌ Gallery load error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Wystąpił błąd podczas ładowania zdjęć';
      setError(errorMessage);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🚀 Gallery component mounted, loading initial images...');
    loadImages(1);
  }, []);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      console.log(`🔄 Loading more images, page ${nextPage}...`);
      setPage(nextPage);
      loadImages(nextPage);
    }
  };

  const handlePrevImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
      
      // Preload more images if we're near the end
      if (selectedImageIndex === images.length - 3 && hasMore && !loading) {
        handleLoadMore();
      }
    }
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => {
            console.log('🔄 Retrying to load images...');
            setPage(1);
            loadImages(1);
          }}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="gallery-container">
        <div className="gallery-row">
          {images.map((image, index) => (
            <div 
              key={`${image.id}-${index}`} 
              className="polaroid"
              onClick={() => {
                console.log(`🖼️ Opening image ${index + 1} of ${images.length}`);
                setSelectedImageIndex(index);
              }}
              style={{
                zIndex: images.length - index,
                marginLeft: index % 4 !== 0 ? '-3rem' : '0'
              }}
            >
              <div className="aspect-[4/5] overflow-hidden bg-white">
                <img
                  src={getImageUrl(image, true)}
                  alt={image.name || image.description || 'Przesłane zdjęcie'}
                  className="w-full h-full object-cover image-hover"
                  loading="lazy"
                  onLoad={() => {
                    console.log(`✅ Image ${index + 1} loaded successfully`);
                  }}
                  onError={(e) => {
                    console.error(`❌ Failed to load image ${index + 1}:`, getImageUrl(image, true));
                    // Fallback to placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5pZSB1ZGHFgm8gc2nEmSB6YcWCYWRvd2HEhyBvYnJhemVrPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {images.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Brak zdjęć do wyświetlenia</p>
          <p className="text-gray-400 text-sm mt-2">Sprawdź połączenie z serwerem lub spróbuj ponownie później</p>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
          <span className="ml-3 text-gray-600">Ładowanie zdjęć...</span>
        </div>
      )}

      {hasMore && !loading && images.length > 0 && appConfig.gallery.enableInfiniteScroll && (
        <div className="flex justify-center py-12">
          <button
            onClick={handleLoadMore}
            className="ripple bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            Załaduj więcej ({images.length} zdjęć załadowanych)
          </button>
        </div>
      )}

      {selectedImageIndex !== null && (
        <ImageModal 
          image={images[selectedImageIndex]}
          onClose={() => setSelectedImageIndex(null)}
          onPrev={selectedImageIndex > 0 ? handlePrevImage : undefined}
          onNext={selectedImageIndex < images.length - 1 ? handleNextImage : undefined}
        />
      )}
    </div>
  );
}